import { createContext, useContext, useEffect, useRef, useState, useCallback, ReactNode } from 'react';
import { User, AuthChangeEvent, Session, EmailOtpType } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { ensureBootstrapAdmin } from '../lib/bootstrapAdmin';
import { withBootstrapRetry, withAbort, isTransportError } from '../lib/bootstrapRetry';
import { UserProfile, CompanyProfile, OnboardingStatus } from '../types/auth';
import { deriveMfaGate, MfaGateState, MfaRequirements } from '../lib/mfaGate';
import { isBackgroundAuthEvent } from '../lib/authEvents';
import { isRepeatedSignup } from '../lib/repeatedSignup';

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  companyProfile: CompanyProfile | null;
  loading: boolean;
  bootstrapError: boolean;
  bootstrapErrorDetail: string | null;
  isAdmin: boolean;
  isDisabled: boolean;
  isPasswordRecovery: boolean;
  mfaGate: MfaGateState;
  mfaRequirements: MfaRequirements | null;
  verifyMfa: (code: string) => Promise<{ error: Error | null }>;
  refreshMfaGate: () => void;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: Error | null; userId?: string | null; alreadyRegistered?: boolean }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  resendConfirmation: (email: string) => Promise<{ error: Error | null }>;
  confirmEmail: (tokenHash: string, type: EmailOtpType) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>;
  refreshProfile: () => Promise<void>;
  retryBootstrap: () => void;
  updateCompanyProfile: (updates: Partial<CompanyProfile>) => Promise<{ error: Error | null }>;
  setOnboardingStatus: (status: OnboardingStatus) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Hard ceiling for the whole bootstrap sequence. Worst-case legitimate bootstrap
// (3 attempts x 8s abort + ~1s/2s backoff) is ~27s, so 30s backstops a genuinely
// hung call (e.g. a getSession that never settles) without pre-empting a retry
// sequence that is still legitimately running.
const BOOTSTRAP_CEILING_MS = 30000;

// Breadcrumb: the phase the bootstrap is currently awaiting. We do not actually
// know which await can never settle in the wild, so record it; when the hard
// ceiling fires we report "stalled at: <phase>" and the next occurrence names
// itself instead of being a mystery.
let bootstrapPhase = 'idle';

// Load a bootstrap-critical row: 8s per-request hang budget + retry on transport
// failure. A resolved PostgREST/HTTP error (carries a code) passes straight
// through so it surfaces immediately; only a transport failure/abort is retried.
async function loadCritical<T>(
  run: (signal: AbortSignal) => PromiseLike<{ data: T; error: unknown }>
): Promise<{ data: T; error: unknown }> {
  return withBootstrapRetry(async () => {
    const res = await withAbort(run);
    if (res.error && isTransportError(res.error)) throw res.error;
    return res;
  });
}

/**
 * "Now" for the MFA re-prompt comparison, in server time where possible.
 *
 * The amr timestamps being compared are stamped by the auth server. Measuring
 * their age with the device clock lets a badly wrong clock manufacture a
 * challenge that re-verifying can never satisfy — the fresh timestamp is still
 * "old" against the skewed clock, so the user loops. The access token's `iat`
 * is server time and is at most one refresh interval behind, which is nothing
 * against a window measured in days. Falls back to the device clock only when
 * the token cannot be read.
 */
async function referenceNowSeconds(): Promise<number> {
  const deviceNow = Math.floor(Date.now() / 1000);
  try {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) return deviceNow;
    const payload = JSON.parse(
      atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
    ) as { iat?: number };
    return typeof payload.iat === 'number' ? payload.iat : deviceNow;
  } catch {
    return deviceNow;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [companyProfile, setCompanyProfile] = useState<CompanyProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [mfaGate, setMfaGateState] = useState<MfaGateState>('none');
  const [mfaRequirements, setMfaRequirements] = useState<MfaRequirements | null>(null);
  // Ref mirror so the auth-state listener (a long-lived closure) always sees
  // the current gate.
  const mfaGateRef = useRef<MfaGateState>('none');
  const setMfaGate = useCallback((g: MfaGateState) => {
    mfaGateRef.current = g;
    setMfaGateState(g);
  }, []);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const [bootstrapError, setBootstrapError] = useState(false);
  const [bootstrapErrorDetail, setBootstrapErrorDetail] = useState<string | null>(null);

  const mountedRef = useRef(true);
  const inFlightRef = useRef(false);
  const runIdRef = useRef(0);
  const ceilingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isAdmin = userProfile?.role === 'admin' && !userProfile?.is_disabled;
  const isDisabled = userProfile?.is_disabled === true;

  // Loads profile + company for a user. Each critical select gets an 8s hang
  // budget and retries transport failures; a real HTTP/PGRST error surfaces in
  // `error` as before. An exhausted transport failure THROWS out to the caller,
  // which decides whether to surface the bootstrap error screen — this function
  // no longer owns the `loading` flag or silently swallows network failures.
  const loadUserProfile = useCallback(async (userId: string) => {
    bootstrapPhase = 'loadUserProfile';
    const { data: profile, error: profileError } = await loadCritical((signal) =>
      supabase.from('user_profiles').select('*').eq('id', userId).abortSignal(signal).maybeSingle()
    );

    if (profileError && (profileError as { code?: string }).code !== 'PGRST116') {
      console.error('Error loading user profile:', profileError);
    }

    // Bootstrap admin: if this user's email is in the allowlist and no
    // admin exists yet, promote them and re-fetch so the UI sees the
    // updated role without requiring a page refresh.
    if (profile) {
      const promoted = await ensureBootstrapAdmin(
        userId,
        profile.email ?? '',
        profile.role ?? 'user'
      );
      if (promoted) {
        // Re-fetch profile to pick up the new role
        const { data: refreshed } = await loadCritical((signal) =>
          supabase.from('user_profiles').select('*').eq('id', userId).abortSignal(signal).maybeSingle()
        );
        setUserProfile(refreshed);
        supabase
          .from('user_profiles')
          .update({ last_active_at: new Date().toISOString() })
          .eq('id', userId)
          .then(() => {});
      } else {
        setUserProfile(profile);
        supabase
          .from('user_profiles')
          .update({ last_active_at: new Date().toISOString() })
          .eq('id', userId)
          .then(() => {});
      }
    } else {
      setUserProfile(profile);
    }

    bootstrapPhase = 'loadCompanyProfile';
    const { data: company, error: companyError } = await loadCritical((signal) =>
      supabase.from('company_profiles').select('*').eq('user_id', userId).abortSignal(signal).maybeSingle()
    );

    if (companyError && (companyError as { code?: string }).code !== 'PGRST116') {
      console.error('Error loading company profile:', companyError);
    }

    setCompanyProfile(company);
  }, []);

  const clearCeiling = useCallback(() => {
    if (ceilingTimerRef.current) {
      clearTimeout(ceilingTimerRef.current);
      ceilingTimerRef.current = null;
    }
  }, []);

  // Decide whether MFA stands between this session and the app shell. Runs
  // BEFORE the profile load: an enrolled user's aal1 session cannot read most
  // rows (restrictive policies), so loading the profile first would misread
  // enforcement as a bootstrap failure. get_mfa_requirements() is SECURITY
  // DEFINER and answers at aal1 by design. If it errors non-transport, we
  // fall back to the factor-based decision alone — the challenge still
  // gates enrolled users, and the database enforces regardless.
  const assessMfaGate = useCallback(async (): Promise<MfaGateState> => {
    bootstrapPhase = 'mfaGate';
    const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();

    let requirements: MfaRequirements | null = null;
    try {
      const { data, error } = await loadCritical<unknown>((signal) =>
        supabase.rpc('get_mfa_requirements').abortSignal(signal)
      );
      if (error) {
        console.error('get_mfa_requirements error (falling back to factor-based gate):', error);
      } else if (data && typeof data === 'object') {
        requirements = data as MfaRequirements;
      }
    } catch (err) {
      // Exhausted transport failure: surface like any bootstrap-critical read.
      throw err;
    }
    if (mountedRef.current) setMfaRequirements(requirements);

    return deriveMfaGate({
      currentLevel: aal?.currentLevel ?? null,
      nextLevel: aal?.nextLevel ?? null,
      requirements,
      amr: aal?.currentAuthenticationMethods ?? null,
      nowSeconds: await referenceNowSeconds(),
    });
  }, []);

  // The bootstrap sequence: resolve the session, then load the profile. Wrapped
  // so it can be re-run by the Retry button. Guarantees an exit on every path —
  // success, transport failure after retries, or the hard ceiling.
  const runBootstrap = useCallback(async () => {
    // Guard: never start a second concurrent bootstrap (e.g. a double Retry click).
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    const runId = ++runIdRef.current;
    const isCurrent = () => mountedRef.current && runId === runIdRef.current;

    if (mountedRef.current) {
      setBootstrapError(false);
      setBootstrapErrorDetail(null);
      setLoading(true);
    }

    const finishLoaded = () => {
      clearCeiling();
      setBootstrapError(false);
      setBootstrapErrorDetail(null);
      setLoading(false);
    };

    // Hard ceiling — guarantees an exit even if a call never settles. It fires for
    // the run it was armed for, and releases the in-flight guard so Retry can start
    // a fresh run past a genuine hang.
    clearCeiling();
    ceilingTimerRef.current = setTimeout(() => {
      if (!mountedRef.current) return;
      console.error(`[bootstrap] hard ceiling (${BOOTSTRAP_CEILING_MS}ms) reached; stalled at: ${bootstrapPhase}`);
      setBootstrapErrorDetail(`stalled at: ${bootstrapPhase}`);
      setBootstrapError(true);
      setLoading(false);
      inFlightRef.current = false;
    }, BOOTSTRAP_CEILING_MS);

    try {
      bootstrapPhase = 'getSession';
      // getSession() is deliberately left untouched (auth/session/token logic).
      // The hard ceiling is what protects against it never settling.
      const { data: { session }, error } = await supabase.auth.getSession();
      if (!isCurrent()) return;

      if (error) {
        console.error('Error getting session:', error);
        setUser(null);
        finishLoaded();
        return;
      }

      setUser(session?.user ?? null);
      if (session?.user) {
        // Mark the gate unresolved BEFORE awaiting the assessment, in the same
        // synchronous block as setUser. Otherwise consumers briefly observe
        // `user != null && loading === false && mfaGate === 'none'` — a state
        // that reads as "signed in, nothing owed" while the session may still
        // be aal1, where the restrictive MFA policies return zero rows with no
        // error and startup work misreads that as an empty account.
        setMfaGate('pending');
        const gate = await assessMfaGate();
        if (!isCurrent()) return;
        setMfaGate(gate);
        if (gate !== 'none') {
          // The MFA screen owns the flow from here; the profile loads after
          // verification re-runs this sequence via the auth-state listener.
          finishLoaded();
          return;
        }
        await loadUserProfile(session.user.id);
        if (!isCurrent()) return;
      } else {
        setMfaGate('none');
        setMfaRequirements(null);
      }
      // A late success after the ceiling fired still resolves normally into the
      // loaded state — recovering beats staying stuck.
      finishLoaded();
    } catch (err) {
      if (!isCurrent()) return;
      if (isTransportError(err)) {
        console.error(`[bootstrap] transport failure after retries; phase: ${bootstrapPhase}`, err);
        setBootstrapErrorDetail(`couldn't reach the server (at ${bootstrapPhase})`);
        setBootstrapError(true);
        setLoading(false);
      } else {
        // A non-transport error must not wedge the app; log and let it render.
        console.error('Error initializing auth:', err);
        finishLoaded();
      }
    } finally {
      if (runId === runIdRef.current) inFlightRef.current = false;
    }
  }, [loadUserProfile, clearCeiling, assessMfaGate]);

  const retryBootstrap = useCallback(() => {
    void runBootstrap();
  }, [runBootstrap]);

  // Re-evaluate the gate and, when it opens, complete the deferred bootstrap.
  // Used after backup-code recovery (factors just vanished server-side) and
  // by the enrollment screen's finish step.
  const refreshMfaGate = useCallback(() => {
    void runBootstrap();
  }, [runBootstrap]);

  // Challenge + verify the caller's enrolled TOTP factor. On success Supabase
  // elevates the session to aal2 and emits MFA_CHALLENGE_VERIFIED, which the
  // auth-state listener turns into a profile load — no orchestration here.
  const verifyMfa = useCallback(async (code: string): Promise<{ error: Error | null }> => {
    try {
      const { data: factors, error: listError } = await supabase.auth.mfa.listFactors();
      if (listError) return { error: listError };
      const totp = factors?.totp?.find(f => f.status === 'verified') ?? factors?.totp?.[0];
      if (!totp) return { error: new Error('No authenticator app is enrolled on this account.') };
      const { error: verifyError } = await supabase.auth.mfa.challengeAndVerify({
        factorId: totp.id,
        code,
      });
      if (verifyError) return { error: verifyError };
      return { error: null };
    } catch (err) {
      return { error: err instanceof Error ? err : new Error('Verification failed.') };
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    void runBootstrap();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event: AuthChangeEvent, session: Session | null) => {
      if (!mountedRef.current) return;

      if (event === 'PASSWORD_RECOVERY') {
        setIsPasswordRecovery(true);
      } else if (event === 'SIGNED_OUT') {
        setIsPasswordRecovery(false);
      }

      // Housekeeping must not disturb a session that is already up.
      //
      // Supabase refreshes the access token on its own timer, roughly hourly.
      // That used to come through here like any other event and raise
      // `loading`, which makes App return its full-screen spinner and unmount
      // the entire shell — losing every piece of component state behind it: the
      // guided demo restarted from step one, the active tab reset, and any edit
      // not yet through the 300ms save debounce was gone. A refreshed token is
      // the same user at the same assurance level, so there is nothing here to
      // re-derive.
      if (isBackgroundAuthEvent(event)) {
        setUser(session?.user ?? null);
        return;
      }

      setUser(session?.user ?? null);

      if (session?.user) {
        if (mfaGateRef.current === 'enroll') {
          // Mid-enrollment the first verified code elevates the session and
          // fires MFA_CHALLENGE_VERIFIED — but the enrollment screen still
          // owes the user their backup codes. Hold the gate; the enrollment
          // flow exits through refreshMfaGate() when the user finishes.
          // (Observed on staging 2026-08-02: the screen unmounted before the
          // codes rendered.)
          if (mountedRef.current) setLoading(false);
          return;
        }
        // Reassess the MFA gate, then keep existing behaviour: refresh the
        // profile on auth-state changes. SIGNED_IN lands here at aal1 for
        // enrolled users (gate → challenge); MFA_CHALLENGE_VERIFIED lands at
        // aal2 (gate → none, profile loads). Rejection-safe and always
        // resolves the loading gate; this path deliberately does NOT raise
        // the full-screen bootstrap error — only initial bootstrap does — so
        // a mid-session token refresh blip stays quiet.
        const userId = session.user.id;
        // Same reasoning as runBootstrap: unresolved until proven otherwise,
        // and `loading` stays raised across the assessment so no consumer acts
        // on a session that may still owe a second factor.
        setMfaGate('pending');
        setLoading(true);
        assessMfaGate()
          .then((gate) => {
            if (!mountedRef.current) return;
            setMfaGate(gate);
            if (gate === 'none') return loadUserProfile(userId);
          })
          .catch((err) => console.error('Error loading profile on auth state change:', err))
          .finally(() => { if (mountedRef.current) setLoading(false); });
      } else {
        setUserProfile(null);
        setCompanyProfile(null);
        setMfaGate('none');
        setMfaRequirements(null);
        setLoading(false);
      }
    });

    return () => {
      mountedRef.current = false;
      inFlightRef.current = false;
      clearCeiling();
      subscription.unsubscribe();
    };
  }, [runBootstrap, loadUserProfile, clearCeiling]);

  async function signUp(email: string, password: string, fullName: string) {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: window.location.origin,
          data: {
            full_name: fullName,
          },
        },
      });

      if (error) throw error;

      if (isRepeatedSignup(data.user)) {
        return { error: null, userId: null, alreadyRegistered: true };
      }

      return { error: null, userId: data.user?.id ?? null, alreadyRegistered: false };
    } catch (error) {
      return { error: error as Error, userId: null, alreadyRegistered: false };
    }
  }

  async function signIn(email: string, password: string) {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  }

  async function resendConfirmation(email: string) {
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: window.location.origin,
        },
      });

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  }

  // Verifies the token_hash from our own /auth/confirm link (see App.tsx) instead
  // of the raw Supabase project domain in {{ .ConfirmationURL }} — mail filters at
  // large corporate domains (observed: nationalgrid.com) treat a branded sender
  // linking to an unrelated *.supabase.co host as a phishing signal and silently
  // quarantine it, even though Resend reports the send as "Delivered". Success
  // establishes a session the same way clicking the old link did.
  async function confirmEmail(tokenHash: string, type: EmailOtpType) {
    try {
      const { error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type });
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  }

  async function signOut() {
    await supabase.auth.signOut();
    setUser(null);
    setUserProfile(null);
    setCompanyProfile(null);
    setIsPasswordRecovery(false);
  }

  async function resetPassword(email: string) {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  }

  async function updatePassword(newPassword: string) {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  }

  async function refreshProfile() {
    if (user) {
      await loadUserProfile(user.id);
    }
  }

  async function updateCompanyProfile(updates: Partial<CompanyProfile>) {
    if (!user) {
      return { error: new Error('No user logged in') };
    }

    try {
      if (companyProfile) {
        const { error } = await supabase
          .from('company_profiles')
          .update(updates)
          .eq('user_id', user.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('company_profiles')
          .insert({
            user_id: user.id,
            ...updates,
          });

        if (error) throw error;
      }

      await refreshProfile();
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  }

  // First-project wizard state. Optimistic: the UI reacts immediately and a
  // failed write only costs re-offering the tour next session, so a plain
  // console error is the right severity.
  async function setOnboardingStatus(status: OnboardingStatus) {
    if (!user) return;
    setUserProfile((p) => (p ? { ...p, onboarding_status: status } : p));
    const { error } = await supabase
      .from('user_profiles')
      .update({ onboarding_status: status })
      .eq('id', user.id);
    if (error) console.error('Failed to save onboarding status:', error);
  }

  const value = {
    user,
    userProfile,
    companyProfile,
    loading,
    bootstrapError,
    bootstrapErrorDetail,
    isAdmin,
    isDisabled,
    isPasswordRecovery,
    mfaGate,
    mfaRequirements,
    verifyMfa,
    refreshMfaGate,
    signUp,
    signIn,
    resendConfirmation,
    confirmEmail,
    signOut,
    resetPassword,
    updatePassword,
    refreshProfile,
    retryBootstrap,
    updateCompanyProfile,
    setOnboardingStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
